import dayjs from "dayjs";
import { useQuery } from "react-query";
import { toast } from "sonner";
import { queryClient } from "../../..";
import {
    _api_get_pilotage_absenteisme,
    _api_get_pilotage_indicators,
    _api_post_pilotage_indicators,
    _api_get_pilotage_diversite,
    _api_get_pilotage_effectif,
    _api_get_pilotage_frais,
    _api_get_pilotage_mouvements,
    _api_get_pilotage_retention,
    _api_get_pilotage_taches,
    _api_get_pilotage_temps_travail,
} from ".";
import {
    QUERY_KEY_PILOTAGE_ABSENTEISME,
    QUERY_KEY_PILOTAGE_DIVERSITE,
    QUERY_KEY_PILOTAGE_EFFECTIF,
    QUERY_KEY_PILOTAGE_FRAIS,
    QUERY_KEY_PILOTAGE_INDICATORS,
    QUERY_KEY_PILOTAGE_MOUVEMENTS,
    QUERY_KEY_PILOTAGE_RETENTION,
    QUERY_KEY_PILOTAGE_TACHES,
    QUERY_KEY_PILOTAGE_TEMPS_TRAVAIL,
} from "./keys";

/** Construit la query string commune aux endpoints /hr/pilotage/* */
export const buildPilotageSearchParams = (
    filters,
    { withPeriod = true } = {}
) => {
    const params = new URLSearchParams();
    if (withPeriod) params.set("period", filters.period);
    if (filters.agency_id) params.append("agency_ids", filters.agency_id);
    if (filters.manager_id) params.append("manager_ids", filters.manager_id);
    if (filters.department_id)
        params.append("department_ids", filters.department_id);
    return params.toString();
};

/** Query string de GET /hr/pilotage/export/synthese (valeurs du formulaire d'export) */
export const buildExportSyntheseSearchParams = (values) => {
    const params = new URLSearchParams();
    params.set("start_date", dayjs(values.start_date).format("YYYY-MM-DD"));
    params.set("end_date", dayjs(values.end_date).format("YYYY-MM-DD"));
    values.agency_ids.forEach((id) => params.append("agency_ids", id));
    values.department_ids.forEach((id) => params.append("department_ids", id));
    values.manager_ids.forEach((id) => params.append("manager_ids", id));
    params.set(
        "sections",
        Object.keys(values.sections)
            .filter((key) => values.sections[key])
            .join(",")
    );
    params.set("compare_by", values.compare_by);
    params.set("export_format", values.export_format);
    return params.toString();
};

/** Query string de GET /hr/pilotage/export/detail-collaborateurs (formulaire d'export + onglet "Détail collaborateurs") */
export const buildExportDetailSearchParams = (
    values,
    { employeeIds, fields }
) => {
    const params = new URLSearchParams();
    params.set("start_date", dayjs(values.start_date).format("YYYY-MM-DD"));
    params.set("end_date", dayjs(values.end_date).format("YYYY-MM-DD"));
    values.agency_ids.forEach((id) => params.append("agency_ids", id));
    values.department_ids.forEach((id) => params.append("department_ids", id));
    values.manager_ids.forEach((id) => params.append("manager_ids", id));
    employeeIds.forEach((id) => params.append("employee_ids", id));
    params.set("fields", fields.join(","));
    params.set("export_format", values.export_format);
    return params.toString();
};

/** Query générique pour une section de pilotage */
const usePilotageQuery = ({
    key,
    apiFn,
    filters,
    errorMessage,
    withPeriod = true,
}) => {
    const searchParams = buildPilotageSearchParams(filters, { withPeriod });

    const { data, isLoading, isFetching, isError } = useQuery(
        [key, searchParams],
        () => apiFn(searchParams),
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            onSuccess: (result) => {
                // fetcher ne throw pas sur 400 : l'erreur est dans le body
                if (result?.error) {
                    toast.error(result?.message || errorMessage);
                }
            },
        }
    );

    return {
        data: data?.error ? null : data,
        isLoading,
        isFetching,
        isError: isError || !!data?.error,
    };
};

export const useQueryPilotageEffectif = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_EFFECTIF,
        apiFn: _api_get_pilotage_effectif,
        filters,
        errorMessage: "Impossible de charger les données d'effectif",
    });

export const useQueryPilotageDiversite = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_DIVERSITE,
        apiFn: _api_get_pilotage_diversite,
        filters,
        errorMessage: "Impossible de charger les données de diversité",
    });

export const useQueryPilotageRetention = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_RETENTION,
        apiFn: _api_get_pilotage_retention,
        filters,
        errorMessage: "Impossible de charger les données de rétention",
    });

export const useQueryPilotageMouvements = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_MOUVEMENTS,
        apiFn: _api_get_pilotage_mouvements,
        filters,
        errorMessage: "Impossible de charger les données de mouvements",
    });

export const useQueryPilotageAbsenteisme = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_ABSENTEISME,
        apiFn: _api_get_pilotage_absenteisme,
        filters,
        errorMessage: "Impossible de charger les données d'absentéisme",
    });

/* Pas de param `period` sur cet endpoint */
export const useQueryPilotageTempsTravail = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_TEMPS_TRAVAIL,
        apiFn: _api_get_pilotage_temps_travail,
        filters,
        withPeriod: false,
        errorMessage: "Impossible de charger les données de temps de travail",
    });

export const useQueryPilotageFrais = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_FRAIS,
        apiFn: _api_get_pilotage_frais,
        filters,
        errorMessage: "Impossible de charger les données de notes de frais",
    });

export const useQueryPilotageTaches = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_TACHES,
        apiFn: _api_get_pilotage_taches,
        filters,
        errorMessage: "Impossible de charger les données de tâches & suivi",
    });

/* ---------- Masquage des indicateurs ---------- */

/** Préférences d'affichage : { groups: [{ code, label, indicators: [{ code, label, visible }] }] } */
export const useQueryPilotageIndicators = () => {
    const { data, isLoading } = useQuery(
        QUERY_KEY_PILOTAGE_INDICATORS,
        _api_get_pilotage_indicators,
        {
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            onSuccess: (result) => {
                if (result?.error) {
                    toast.error(
                        result?.message ||
                            "Impossible de charger vos préférences d'affichage"
                    );
                }
            },
        }
    );

    return { data: data?.error ? null : data, isLoading };
};

/** Applique `visible` aux indicateurs ciblés par `{ type, code }` */
const applyVisibility = (data, { type, code, visible }) => ({
    ...data,
    groups: data.groups.map((group) => ({
        ...group,
        indicators: group.indicators.map((indicator) =>
            (type === "group" && group.code === code) ||
            (type === "indicator" && indicator.code === code)
                ? { ...indicator, visible }
                : indicator
        ),
    })),
});

/** Aligne le cache sur la liste des codes masqués renvoyée par le serveur */
const applyHiddenList = (data, hiddenIndicators) => ({
    ...data,
    groups: data.groups.map((group) => ({
        ...group,
        indicators: group.indicators.map((indicator) => ({
            ...indicator,
            visible: !hiddenIndicators.includes(indicator.code),
        })),
    })),
});

/* Requêtes POST en vol : on ne réconcilie le cache qu'après la dernière,
   sinon une réponse intermédiaire écraserait une mise à jour optimiste plus récente. */
let pendingIndicatorUpdates = 0;

/**
 * POST /hr/pilotage/indicators avec mise à jour optimiste du cache.
 * Fonction (et non useMutation) : elle peut être appelée depuis un toast
 * après le démontage de la carte masquée.
 * Retourne true si le serveur a bien enregistré la préférence.
 */
export const updatePilotageIndicator = async (body) => {
    const previous = queryClient.getQueryData(QUERY_KEY_PILOTAGE_INDICATORS);
    if (!previous?.groups) return false;

    await queryClient.cancelQueries(QUERY_KEY_PILOTAGE_INDICATORS);
    queryClient.setQueryData(
        QUERY_KEY_PILOTAGE_INDICATORS,
        applyVisibility(previous, body)
    );

    pendingIndicatorUpdates += 1;
    let result;
    try {
        result = await _api_post_pilotage_indicators(body);
    } catch (e) {
        result = null;
    }
    pendingIndicatorUpdates -= 1;

    if (!Array.isArray(result?.hidden_indicators)) {
        toast.error(
            result?.message ||
                "Impossible d'enregistrer vos préférences d'affichage"
        );
        queryClient.invalidateQueries(QUERY_KEY_PILOTAGE_INDICATORS);
        return false;
    }

    if (pendingIndicatorUpdates === 0) {
        queryClient.setQueryData(QUERY_KEY_PILOTAGE_INDICATORS, (current) =>
            current?.groups
                ? applyHiddenList(current, result.hidden_indicators)
                : current
        );
    }
    return true;
};
